import { createTheme, alpha } from "@mui/material/styles";

/**
 * Playbook design system
 * Brand primary (CTA / accent): #99060E
 * Brand secondary (navy chrome): #0E1C2C
 *
 * MUI mapping (matches existing app usage patterns):
 * - palette.primary  → navy surfaces, headings, body brand color
 * - palette.secondary → red CTAs, accents, icon highlights, hovers
 */
const PLAYBOOK = {
  primary: "#99060E",
  secondary: "#0E1C2C",
  background: "#F8F6F2",
  surface: "#FFFFFF",
  textPrimary: "#0E1C2C",
  textSecondary: "#5E6773",
  border: "#E5E7EB",
  primaryDark: "#7A050B",
  primarySoft: alpha("#99060E", 0.12),
  navySoft: alpha("#0E1C2C", 0.08),
};

const brandTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: PLAYBOOK.secondary,
      dark: "#071018",
      light: "#1A2F45",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: PLAYBOOK.primary,
      dark: PLAYBOOK.primaryDark,
      light: "#B81720",
      contrastText: "#FFFFFF",
    },
    background: {
      default: PLAYBOOK.background,
      paper: PLAYBOOK.surface,
    },
    text: {
      primary: PLAYBOOK.textPrimary,
      secondary: PLAYBOOK.textSecondary,
    },
    divider: PLAYBOOK.border,
    grey: {
      50: "#FAFAF8",
      100: PLAYBOOK.background,
      200: PLAYBOOK.border,
      300: "#D1D5DB",
      400: "#9CA3AF",
      500: PLAYBOOK.textSecondary,
      600: "#4B5563",
      700: "#374151",
      800: PLAYBOOK.secondary,
      900: "#071018",
    },
    error: {
      main: PLAYBOOK.primary,
    },
  },
  brand: {
    primary: PLAYBOOK.primary,
    secondary: PLAYBOOK.secondary,
    background: PLAYBOOK.background,
    surface: PLAYBOOK.secondary,
    surfaceLight: PLAYBOOK.surface,
    textOnSurface: "#FFFFFF",
    textPrimary: PLAYBOOK.textPrimary,
    textSecondary: PLAYBOOK.textSecondary,
    border: PLAYBOOK.border,
    link: "#FFFFFF",
    linkHover: PLAYBOOK.primary,
    accent: PLAYBOOK.primary,
    accentSoft: PLAYBOOK.primarySoft,
    navySoft: PLAYBOOK.navySoft,
    cta: PLAYBOOK.primary,
    ctaHover: PLAYBOOK.primaryDark,
  },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    h1: { fontWeight: 700, letterSpacing: "-0.02em", color: PLAYBOOK.textPrimary },
    h2: { fontWeight: 700, letterSpacing: "-0.02em", color: PLAYBOOK.textPrimary },
    h3: { fontWeight: 700, letterSpacing: "-0.015em", color: PLAYBOOK.textPrimary },
    h4: { fontWeight: 700, letterSpacing: "-0.01em", color: PLAYBOOK.textPrimary },
    h5: { fontWeight: 600, color: PLAYBOOK.textPrimary },
    h6: { fontWeight: 600, color: PLAYBOOK.textPrimary },
    subtitle1: { fontWeight: 500, color: PLAYBOOK.textPrimary },
    subtitle2: { fontWeight: 500, color: PLAYBOOK.textSecondary },
    body1: { fontWeight: 400, lineHeight: 1.6, color: PLAYBOOK.textPrimary },
    body2: { fontWeight: 400, lineHeight: 1.55, color: PLAYBOOK.textSecondary },
    button: {
      fontWeight: 600,
      textTransform: "none",
      letterSpacing: "0.01em",
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0 1px 2px rgba(14, 28, 44, 0.04), 0 1px 3px rgba(14, 28, 44, 0.06)",
    "0 2px 8px rgba(14, 28, 44, 0.06), 0 1px 2px rgba(14, 28, 44, 0.04)",
    "0 4px 16px rgba(14, 28, 44, 0.08), 0 2px 4px rgba(14, 28, 44, 0.04)",
    "0 8px 24px rgba(14, 28, 44, 0.1), 0 2px 6px rgba(14, 28, 44, 0.05)",
    "0 12px 32px rgba(14, 28, 44, 0.12), 0 4px 8px rgba(14, 28, 44, 0.06)",
    "0 16px 40px rgba(14, 28, 44, 0.14)",
    "0 20px 48px rgba(14, 28, 44, 0.16)",
    "0 24px 56px rgba(14, 28, 44, 0.18)",
    "0 1px 2px rgba(14, 28, 44, 0.04), 0 1px 3px rgba(14, 28, 44, 0.06)",
    "0 2px 8px rgba(14, 28, 44, 0.06)",
    "0 4px 16px rgba(14, 28, 44, 0.08)",
    "0 8px 24px rgba(14, 28, 44, 0.1)",
    "0 12px 32px rgba(14, 28, 44, 0.12)",
    "0 16px 40px rgba(14, 28, 44, 0.14)",
    "0 20px 48px rgba(14, 28, 44, 0.16)",
    "0 24px 56px rgba(14, 28, 44, 0.18)",
    "0 24px 56px rgba(14, 28, 44, 0.18)",
    "0 24px 56px rgba(14, 28, 44, 0.18)",
    "0 24px 56px rgba(14, 28, 44, 0.18)",
    "0 24px 56px rgba(14, 28, 44, 0.18)",
    "0 24px 56px rgba(14, 28, 44, 0.18)",
    "0 24px 56px rgba(14, 28, 44, 0.18)",
    "0 24px 56px rgba(14, 28, 44, 0.18)",
    "0 24px 56px rgba(14, 28, 44, 0.18)",
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: PLAYBOOK.background,
          color: PLAYBOOK.textPrimary,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 999,
          paddingLeft: 22,
          paddingRight: 22,
          paddingTop: 10,
          paddingBottom: 10,
          fontWeight: 600,
          transition: "background-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease, border-color 0.2s ease",
          "&:hover": {
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
        containedPrimary: {
          backgroundColor: PLAYBOOK.primary,
          color: "#FFFFFF",
          boxShadow: `0 4px 14px ${alpha(PLAYBOOK.primary, 0.28)}`,
          "&:hover": {
            backgroundColor: PLAYBOOK.primaryDark,
            boxShadow: `0 6px 18px ${alpha(PLAYBOOK.primary, 0.35)}`,
          },
        },
        containedSecondary: {
          backgroundColor: PLAYBOOK.secondary,
          color: "#FFFFFF",
          "&:hover": {
            backgroundColor: "#071018",
          },
        },
        outlinedPrimary: {
          borderColor: PLAYBOOK.border,
          color: PLAYBOOK.secondary,
          "&:hover": {
            borderColor: PLAYBOOK.primary,
            backgroundColor: PLAYBOOK.primarySoft,
            color: PLAYBOOK.primary,
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: "transparent",
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderBottom: `1px solid ${alpha("#FFFFFF", 0.08)}`,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderRadius: 16,
        },
        elevation1: {
          boxShadow: "0 2px 8px rgba(14, 28, 44, 0.06), 0 1px 2px rgba(14, 28, 44, 0.04)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${PLAYBOOK.border}`,
          boxShadow: "0 2px 8px rgba(14, 28, 44, 0.05)",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: PLAYBOOK.surface,
          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(PLAYBOOK.secondary, 0.35),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: PLAYBOOK.primary,
            borderWidth: 1.5,
          },
        },
        notchedOutline: {
          borderColor: PLAYBOOK.border,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: "background-color 0.2s ease, color 0.2s ease",
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: PLAYBOOK.primary,
          textDecorationColor: alpha(PLAYBOOK.primary, 0.35),
          transition: "color 0.2s ease",
          "&:hover": {
            color: PLAYBOOK.primaryDark,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: PLAYBOOK.border,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          border: `1px solid ${PLAYBOOK.border}`,
          boxShadow: "0 16px 40px rgba(14, 28, 44, 0.16)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${PLAYBOOK.border}`,
          boxShadow: "0 8px 24px rgba(14, 28, 44, 0.1)",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginLeft: 6,
          marginRight: 6,
          transition: "background-color 0.15s ease",
          "&.Mui-selected": {
            backgroundColor: PLAYBOOK.primarySoft,
            color: PLAYBOOK.primary,
            "&:hover": {
              backgroundColor: alpha(PLAYBOOK.primary, 0.18),
            },
          },
          "&:hover": {
            backgroundColor: PLAYBOOK.navySoft,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: "background-color 0.15s ease",
          "&:hover": {
            backgroundColor: PLAYBOOK.navySoft,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
        filledPrimary: {
          backgroundColor: PLAYBOOK.secondary,
        },
        filledSecondary: {
          backgroundColor: PLAYBOOK.primary,
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
        },
        contained: {
          "&.Mui-selected": {
            backgroundColor: PLAYBOOK.primary,
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: PLAYBOOK.primaryDark,
            },
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: PLAYBOOK.primary,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: PLAYBOOK.primary,
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          "&.Mui-selected": {
            color: PLAYBOOK.primary,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: PLAYBOOK.secondary,
          borderRadius: 8,
          fontSize: "0.75rem",
          fontWeight: 500,
        },
      },
    },
  },
});

export default brandTheme;
export { PLAYBOOK };
